"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.MapLine = void 0;
exports.getSpanLineColor = getSpanLineColor;
const core_api_1 = require("@ikegps/core-api");
const core_shared_1 = require("@ikegps/core-shared");
const theme_1 = require("../../../components/theme");
const theme_palette_1 = require("../../../components/theme.palette");
const mithril_1 = require("../../../mithril");
const services_1 = require("../../../services");
const classnames_1 = require("../../../util/classnames");
const projection_cache_1 = require("../../google/projection.cache");
const LineCSS = {
    container: 'c-TopologicalEditorMapLine',
    vector: 'c-TopologicalEditorMapLine__vector',
    line: 'c-TopologicalEditorMapLine__line',
    highlight: 'c-TopologicalEditorMapLine__highlight',
    lineSelected: 'c-TopologicalEditorMapLine__line--selected',
    label: 'c-TopologicalEditorMapLine__label',
    labelText: 'c-TopologicalEditorMapLine__labelText',
    clickTarget: 'c-TopologicalEditorMapLine__clickTarget',
    handle: 'c-TopologicalEditorMapLine__handle',
    dragging: 'c-TopologicalEditorMapLine--dragging',
    labelSpan: `c-TopologicalEditorMapLine__label--span`,
    labelDropspan: `c-TopologicalEditorMapLine__label--dropSpan`,
    labelVectorAnchor: `c-TopologicalEditorMapLine__label--vectorAnchor`,
    labelAnchor: `c-TopologicalEditorMapLine__label--anchor`
};
const Styles = {
    [LineCSS.container]: {
        pointerEvents: 'none',
        transform: 'translate3d(0, 0, -1px)',
        [`&:hover .${LineCSS.highlight}`]: {
            opacity: 1
        }
    },
    [LineCSS.clickTarget]: {
        cursor: 'pointer',
        pointerEvents: 'all'
    },
    [LineCSS.highlight]: {
        opacity: 0,
        transition: 'opacity 150ms ease',
        transform: 'translate3d(0, 0, -1px)'
    },
    [`${LineCSS.lineSelected} .${LineCSS.highlight}`]: {
        opacity: 1
    },
    [`${LineCSS.lineSelected} .${LineCSS.label}`]: {
        opacity: 1
    },
    [LineCSS.label]: {
        cursor: 'pointer',
        position: 'absolute',
        transform: 'translate3d(-50%, -100%, -1px)',
        opacity: 0.75
    },
    [LineCSS.labelText]: {
        padding: '2px 4px',
        cursor: 'pointer',
        pointerEvents: 'all',
        transformOrigin: '50% 50%',
        color: (0, theme_1.rgba)(theme_palette_1.BLACK),
        background: getSpanLineColor('unknown'),
        transition: 'background 150ms ease'
    },
    [LineCSS.handle]: {
        cursor: 'crosshair',
        userSelect: 'none',
        transform: 'translate3d(0, 0, -1px)',
        pointerEvents: 'all'
    },
    [`${LineCSS.dragging} .${LineCSS.handle}`]: {
        cursor: 'move'
    },
    [LineCSS.labelSpan]: {
        background: getSpanLineColor('span')
    },
    [LineCSS.labelDropspan]: {
        background: getSpanLineColor('dropspan')
    },
    [LineCSS.labelVectorAnchor]: {
        background: getSpanLineColor('vector-anchor')
    },
    [LineCSS.labelAnchor]: {
        background: getSpanLineColor('anchor')
    }
};
(0, theme_1.addStyles)(Styles);
function getSpanType(field) {
    if (['span', 'spans'].includes(field.parent.schema.displayName.toLowerCase())) {
        if (field.provider === 'collection') {
            return 'span';
        }
        return 'dropspan';
    }
    if (field.parent.schema.displayName.toLowerCase() === 'anchor') {
        return 'vector-anchor';
    }
    if (core_shared_1.CollectionModelFieldAnchor.isAnchorField(field)) {
        return 'anchor';
    }
    return 'unknown';
}
function getSpanLineColor(kind, opacity = 1) {
    let color;
    switch (kind) {
        case 'span':
            color = theme_palette_1.ColorPaletteOrange.C400;
            break;
        case 'dropspan':
            color = theme_palette_1.ColorPaletteYellow.C400;
            break;
        case 'vector-anchor':
            color = theme_palette_1.ColorPaletteBlue.C400;
            break;
        case 'anchor':
            color = theme_palette_1.ColorPaletteBlue.A700;
            break;
        default:
            color = theme_palette_1.ColorPaletteGrey.C800;
            break;
    }
    return (0, theme_1.rgba)(color, opacity);
}
class MapLine {
    constructor(opts) {
        this.opts = opts;
        this.kind = getSpanType(opts.vector.field);
        this.terminalPoint = projection_cache_1.PointCache.getPoint(opts.vector.target.latitude, opts.vector.target.longitude);
        const [topLeft, bottomRight] = this.boundingRect;
        this.width = Math.abs(topLeft.x - bottomRight.x);
        this.height = Math.abs(topLeft.y - bottomRight.y);
        this.length = Math.hypot(this.width, this.height);
        this.angle = Math.atan2(this.terminalPoint.y - opts.initialPoint.y, this.terminalPoint.x - opts.initialPoint.x);
        this.leftPx = `${opts.initialPoint.x}px`;
    }
    get calculatedTerminalPoint() {
        return this.opts.isSelected
            ? {
                x: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
                y: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y
            }
            : this.terminalPoint;
    }
    get boundingRect() {
        return [
            new google.maps.Point(Math.min(this.opts.initialPoint.x, this.calculatedTerminalPoint.x), Math.min(this.opts.initialPoint.y, this.calculatedTerminalPoint.y)),
            new google.maps.Point(Math.max(this.opts.initialPoint.x, this.calculatedTerminalPoint.x), Math.max(this.opts.initialPoint.y, this.calculatedTerminalPoint.y))
        ];
    }
    get lineDimensions() {
        return {
            x1: this.opts.initialPoint.x,
            y1: this.opts.initialPoint.y,
            x2: this.calculatedTerminalPoint.x,
            y2: this.calculatedTerminalPoint.y
        };
    }
    get clickTarget() {
        return (0, mithril_1.m)('line', Object.assign({ className: LineCSS.clickTarget, 'stroke-width': '12px', stroke: 'rgba(0,0,0,0)', role: 'button', onclick: this.opts.onClick }, this.lineDimensions));
    }
    get line() {
        return (0, mithril_1.m)('g', [
            (0, mithril_1.m)('line', Object.assign({ className: LineCSS.highlight, stroke: (0, theme_1.rgba)(theme_palette_1.WHITE), 'stroke-width': '4px', 'stroke-dasharray': '2px 2px' }, this.lineDimensions)),
            (0, mithril_1.m)('line', Object.assign({ className: LineCSS.line, stroke: getSpanLineColor(this.kind), 'stroke-width': '2px' }, this.lineDimensions))
        ]);
    }
    get handle() {
        let handleEl = null;
        if (this.opts.isSelected && this.kind !== 'span') {
            handleEl = (0, mithril_1.m)('circle', {
                className: LineCSS.handle,
                cx: this.terminalPoint.x + this.opts.vectorHandleMoveDelta.x,
                cy: this.terminalPoint.y + this.opts.vectorHandleMoveDelta.y,
                r: 4,
                fill: (0, theme_1.rgba)(theme_palette_1.WHITE),
                stroke: (0, theme_1.rgba)(theme_palette_1.BLACK),
                'stroke-width': '1px',
                onmousedown: this.opts.onVectorHandleMouseDown
            });
        }
        return handleEl;
    }
    get label() {
        if (this.opts.showVectorLabel || this.opts.drawLabels) {
            let distance = 0;
            let bearing = 0;
            if (core_shared_1.CollectionModelFieldAnchor.isAnchorField(this.opts.vector.field)) {
                const distanceBearingPitch = this.opts.vector.field.distanceBearingPitch(this.opts.vector.source);
                distance = distanceBearingPitch.distance;
                bearing = distanceBearingPitch.bearing;
            }
            else {
                const newVector = core_shared_1.IkeGeodesic.getVector(this.opts.vector.source, this.opts.vector.target);
                distance = newVector.distance;
                bearing = newVector.bearing;
            }
            const [topLeft, bottomRight] = this.boundingRect;
            const lineAngle = Math.atan2(this.calculatedTerminalPoint.y - this.opts.initialPoint.y, this.calculatedTerminalPoint.x - this.opts.initialPoint.x);
            let textAngle = lineAngle;
            // Don't render upside down text
            if (textAngle > Math.PI / 2 || textAngle < -Math.PI / 2) {
                textAngle = Math.PI + lineAngle;
            }
            const midPoint = {
                x: (topLeft.x + bottomRight.x) / 2,
                y: (topLeft.y + bottomRight.y) / 2
            };
            return (0, mithril_1.m)('div', {
                className: LineCSS.label,
                style: {
                    left: `${midPoint.x}px`,
                    top: `${midPoint.y}px`
                }
            }, (0, mithril_1.m)('div', {
                className: (0, classnames_1.classNames)(LineCSS.labelText, {
                    [LineCSS.labelVectorAnchor]: this.kind === 'vector-anchor',
                    [LineCSS.labelAnchor]: this.kind === 'anchor',
                    [LineCSS.labelSpan]: this.kind === 'span',
                    [LineCSS.labelDropspan]: this.kind === 'dropspan'
                }),
                style: {
                    transform: `rotate(${textAngle}rad) translateZ(-1px)`
                },
                onclick: this.opts.onClick
            }, core_shared_1.IkeUnit.display(distance, services_1.Services.UserSettings.units) +
                ` ${core_shared_1.IkeUnit.displayAngle(bearing, core_api_1.IkeUnitAngle.DEGREE)}`));
        }
        return null;
    }
    get nodes() {
        return (0, mithril_1.m)('div', {
            className: (0, classnames_1.classNames)(LineCSS.container, {
                [LineCSS.lineSelected]: this.opts.isSelected
            }),
            onmouseover: this.opts.onVectorMouseOver,
            onmouseout: this.opts.onVectorMouseOut
        }, [
            (0, mithril_1.m)('svg', {
                className: LineCSS.vector,
                style: {
                    overflow: 'visible',
                    position: 'absolute'
                }
            }, [
                this.line,
                this.clickTarget,
                this.handle
            ]),
            this.label
        ]);
    }
}
exports.MapLine = MapLine;
//# sourceMappingURL=map.line.js.map